import { startAuthentication, startRegistration } from "@simplewebauthn/browser";
import { signInWithCustomToken } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../lib/firebase";

export const USERNAME_PATTERN = /^[a-z][a-z0-9_]{2,23}$/;
export const USERNAME_EMAIL_DOMAIN = "accounts.dickerticker.invalid";

export function normalizeUsername(value: string) {
  return value.trim().toLowerCase();
}

export function usernameEmail(value: string) {
  return `${normalizeUsername(value)}@${USERNAME_EMAIL_DOMAIN}`;
}

async function call<Req, Res>(name: string, data?: Req): Promise<Res> {
  const result = await httpsCallable<Req, Res>(functions, name)(data as Req);
  return result.data;
}

export async function registerUsernameAccount(username: string, password: string) {
  const result = await call<{ username: string; password: string }, { token: string }>("registerUsernameAccount", { username, password });
  return signInWithCustomToken(auth, result.token);
}

export async function changeUsername(username: string) {
  return call<{ username: string }, { username: string }>("changeUsername", { username });
}

export async function signInWithPasskey() {
  const begin = await call<{ action: "begin" }, { challengeId: string; options: Parameters<typeof startAuthentication>[0]["optionsJSON"] }>("passkeyLogin", { action: "begin" });
  const response = await startAuthentication({ optionsJSON: begin.options });
  const finish = await call<{ action: "finish"; challengeId: string; response: typeof response }, { token: string }>("passkeyLogin", { action: "finish", challengeId: begin.challengeId, response });
  return signInWithCustomToken(auth, finish.token);
}

export async function registerPasskey(name: string) {
  const begin = await call<Record<string, never>, { challengeId: string; options: Parameters<typeof startRegistration>[0]["optionsJSON"] }>("beginPasskeyRegistration", {});
  const response = await startRegistration({ optionsJSON: begin.options });
  return call<{ challengeId: string; response: typeof response; name: string }, { id: string; name: string }>("finishPasskeyRegistration", { challengeId: begin.challengeId, response, name });
}

export async function unlockWithPasskey(uid: string) {
  const begin = await call<{ action: "begin" }, { challengeId: string; options: Parameters<typeof startAuthentication>[0]["optionsJSON"] }>("quickUnlock", { action: "begin" });
  const response = await startAuthentication({ optionsJSON: begin.options });
  const finish = await call<{ action: "finish"; challengeId: string; response: typeof response }, { verified: boolean; uid: string }>("quickUnlock", { action: "finish", challengeId: begin.challengeId, response });
  if (!finish.verified || finish.uid !== uid) throw new Error("That passkey does not unlock this account.");
  return finish;
}

export interface PasskeySummary {
  id: string;
  name: string;
  backedUp: boolean;
  createdAt: string | null;
  lastUsedAt: string | null;
}

export async function listPasskeys() {
  return call<Record<string, never>, { passkeys: PasskeySummary[] }>("listPasskeys", {});
}

export async function deletePasskey(id: string) {
  return call<{ id: string }, { deleted: true }>("deletePasskey", { id });
}

export async function deleteAuthArtifacts() {
  return call<Record<string, never>, { deleted: true }>("deleteAuthArtifacts", {});
}
